/**
 * What happens to a filing made on the date on screen.
 *
 * The rows are composed from the structured values the server returned — the spine's segments and
 * renewal or annuity dates, and each computed deadline with its basis and its reason. No date on
 * this screen is calculated here.
 */

import { SectionCard, Legend } from '~/ui/Surface';
import { EmptyState } from '~/ui/Feedback';
import { Chronology, type ChronologyEvent } from './Chronology';
import { NeedsDatePrompt, OPTIONAL_DATES } from './KnownDatesBar';
import { WithheldBlock } from './Withheld';
import { useMatterView } from './useMatterView';
import { vocabularyOf } from './describe';
import { describeReason, missingDateFor } from './reasons';
import { durationBetween, fmtDate, lowerLabel, plural } from '~/lib/format';
import styles from './Matter.module.css';

const SUPPLIED_DATES = new Set(OPTIONAL_DATES.map((entry) => entry.key as string));

export function TimelineTab() {
  const { panel, meta, code, matter, dates, life, lifePending, lifeFromSuppliedDates } =
    useMatterView();
  const vocab = vocabularyOf(meta.data);

  if (!panel.data || !life) return null;

  const events: ChronologyEvent[] = [
    {
      id: 'filing',
      date: life.filing,
      kind: 'filing',
      title: `Filed at ${code}`,
      meta: `${panel.data.label} — every rule below is read against this date.`,
      segment: 'first',
    },
  ];

  if (life.spine) {
    const spine = life.spine;
    events.push({
      id: `spine-base-${spine.ruleId}`,
      date: spine.baseOn,
      kind: 'registration',
      title: `${spine.lane} runs from here`,
      meta: `The series is counted from ${lowerLabel(vocab.triggerLabel(spine.shape === 'term' ? 'registration' : 'filing'))} on this date.`,
      segment: 'first',
    });

    for (const [index, date] of spine.renewalDates.entries()) {
      events.push({
        id: `renewal-${index}`,
        date,
        kind: 'renewal',
        title: `Renewal ${index + 1} falls due`,
        meta: `${spine.lane}, cycle ${index + 1}.`,
        segment: 'renewal',
      });
    }

    for (const [year, date] of spine.annuityDates) {
      events.push({
        id: `annuity-${year}`,
        date,
        kind: 'annuity',
        title: `Annuity for year ${year}`,
        meta: spine.lane,
        segment: 'renewal',
      });
    }

    if (spine.expiryOn) {
      events.push({
        id: 'expiry',
        date: spine.expiryOn,
        kind: 'expiry',
        title: 'The right expires',
        meta: spine.truncated
          ? 'The series hit the engine’s safety bound rather than a legal one.'
          : `${spine.lane}, ${plural(spine.segments.length, 'term')} in all.`,
        segment: 'renewal',
      });
    }
  }

  for (const deadline of life.deadlines) {
    if (!deadline.date) continue;
    events.push({
      id: `deadline-${deadline.ruleId}-${deadline.cycle ?? 0}-${deadline.date}`,
      date: deadline.date,
      kind: 'deadline',
      title: deadline.lane,
      meta: `Counted from ${lowerLabel(vocab.triggerLabel(deadline.basis))}${deadline.cycle ? `, cycle ${deadline.cycle}` : ''}.`,
      segment: 'renewal',
    });
  }

  events.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const refused = life.deadlines.filter((deadline) => deadline.date === null);

  return (
    <>
      <SectionCard
        title="What follows from this filing"
        note="The rail states its own length rather than drawing to scale: the intervals in a right's life run from days to decades, and a proportional axis could not show both."
        maxWidth={1034}
      >
        {lifePending ? (
          <p className={styles.filingHint} role="status">
            Recomputing from the dates you have supplied…
          </p>
        ) : null}
        {events.length <= 1 && refused.length === 0 ? (
          <EmptyState variant="bare">
            <p>
              The register holds no rule that produces a date for this filing. The Rules tab lists
              every rule it holds and every rule it has never adopted, each with the reason.
            </p>
          </EmptyState>
        ) : (
          <>
            <Chronology
              aria-label="What follows from this filing"
              events={events}
              gapLabel={(from, to) =>
                from.date === to.date ? null : durationBetween(from.date, to.date)
              }
            />
            <Legend
              aria-label="What each marker means"
              items={[
                { key: <span className={styles.dot} aria-hidden="true" />, label: 'Filing' },
                {
                  key: <span className={[styles.dot, styles.dotRenewal].join(' ')} aria-hidden="true" />,
                  label: 'Renewal or expiry',
                },
                {
                  key: <span className={[styles.dot, styles.dotDeadline].join(' ')} aria-hidden="true" />,
                  label: 'Deadline',
                },
              ]}
            />
          </>
        )}
      </SectionCard>

      {refused.length > 0 ? (
        <SectionCard
          title="Dates the engine would not produce"
          note="A rule that produces nothing for this filing is still part of the answer, and so is the reason."
          maxWidth={1034}
        >
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {refused.map((deadline) => {
              const basisLabel = vocab.triggerLabel(deadline.basis);
              // A refusal that names a date the user can supply is a prompt for that field.
              const missing = missingDateFor(deadline.reason);
              const askable = missing !== null && SUPPLIED_DATES.has(missing);
              return (
                <li key={`${deadline.ruleId}-${deadline.basis}-${deadline.cycle ?? 0}`}>
                  <div className={styles.ruleHead}>
                    <h3>{deadline.lane}</h3>
                    <span className={styles.spacer} />
                    {askable ? (
                      <NeedsDatePrompt basis={missing} label={vocab.triggerLabel(missing)} />
                    ) : (
                      <span className={styles.resultNone}>
                        {describeReason(deadline.reason, vocab)}
                      </span>
                    )}
                  </div>
                  <p className={styles.calc}>
                    Counted from {lowerLabel(basisLabel)}.
                    {askable
                      ? ` The engine has not been given the ${lowerLabel(vocab.triggerLabel(missing))} date, so it will not guess one.`
                      : ''}
                  </p>
                  {deadline.contradiction ? (
                    <WithheldBlock
                      code={code}
                      matter={matter}
                      lane={deadline.lane}
                      versionId={deadline.ruleVersionId}
                      contradiction={deadline.contradiction}
                      dateParams={dates as Record<string, string | undefined>}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </SectionCard>
      ) : null}

      {life.gaps.length > 0 ? (
        <SectionCard
          title="Rules with no version in force on this date"
          note="The register holds the rule; no version of it governs the filing on screen."
          maxWidth={1034}
        >
          <ul style={{ paddingLeft: 18 }}>
            {life.gaps.map((gap) => (
              <li key={gap.ruleId} style={{ marginTop: 6 }}>
                <strong>{gap.lane}</strong> — {describeReason(gap.reason, vocab)}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      <p className={styles.filingHint}>
        Computed for a filing on {fmtDate(life.filing)}
        {lifeFromSuppliedDates
          ? ', together with the other dates you have supplied above.'
          : '. Supply the other dates above and the deadlines counted from them will follow.'}
      </p>
    </>
  );
}
